// src/stores/addressStore.js
import { defineStore } from 'pinia';
import http from '../../common/utils/http-common';
import { useTenantStore } from './tenant';
import { ref } from 'vue';
import * as Sentry from '@sentry/vue';

export const addressFieldsMap = {
    tenant: {
        addressCode: 'start_addressCode',
        city: 'start_city',
        streetName: 'start_streetName',
        houseNumber: 'start_houseNumber'
    },
    property: {
        addressCode: 'next_propertyAddressCode',
        city: 'next_propertyCity',
        streetName: 'next_propertyStreetName',
        houseNumber: 'next_propertyHouseNumber'
    },
    landlord: {
        addressCode: 'next_landlordAddressCode',
        city: 'next_landlordCity',
        streetName: 'next_landlordStreetName',
        houseNumber: 'next_landlordHouseNumber'
    }
};

export const useAddressStore = defineStore('addressStore', () => {
    const tenantStore = useTenantStore();

    const suggestions = ref({
        tenant: {
            addressCodeSuggestions: [],
            streetNameSuggestions: [],
            citySuggestions: [],
            houseNumberSuggestions: []
        },
        property: {
            addressCodeSuggestions: [],
            streetNameSuggestions: [],
            citySuggestions: [],
            houseNumberSuggestions: []
        },
        landlord: {
            addressCodeSuggestions: [],
            streetNameSuggestions: [],
            citySuggestions: [],
            houseNumberSuggestions: []
        }
    });

    const isDropdownOpen = ref({
        tenant: {
            addressCode: false,
            streetName: false,
            city: false,
            houseNumber: false
        },
        property: {
            addressCode: false,
            streetName: false,
            city: false,
            houseNumber: false
        },
        landlord: {
            addressCode: false,
            streetName: false,
            city: false,
            houseNumber: false
        }
    });

    const toggleDropdown = (addressType, field, show) => {
        if (show) {
            isDropdownOpen.value[addressType][field] = true;
        } else {
            setTimeout(() => {
                isDropdownOpen.value[addressType][field] = false;
            }, 200);
        }
    };

    const getAutocompleteSuggestions = async (addressType, type) => {
        const input = [
            tenantStore.formValues[addressFieldsMap[addressType].addressCode],
            tenantStore.formValues[addressFieldsMap[addressType].streetName],
            tenantStore.formValues[addressFieldsMap[addressType].city]
        ].filter(Boolean).join(" ");

        Sentry.addBreadcrumb({
            category: 'api',
            message: `Requesting autocomplete suggestions for ${input}`,
            level: 'info'
        });

        try {
            const response = await http.get('/api/places/suggestions', { params: { input, type } });
            const suffix = ", Deutschland";
            const cleanedPredictions = response.data.predictions.map(prediction => {
                let description = prediction.description;
                if (description.endsWith(suffix)) {
                    description = description.substring(0, description.length - suffix.length);
                }
                return { ...prediction, description };
            });

            switch (type) {
                case 'postal_code':
                    suggestions.value[addressType].addressCodeSuggestions = cleanedPredictions;
                    break;
                case 'route':
                    suggestions.value[addressType].streetNameSuggestions = cleanedPredictions;
                    break;
                case 'locality':
                    suggestions.value[addressType].citySuggestions = cleanedPredictions;
                    break;
                case 'street_number':
                    suggestions.value[addressType].houseNumberSuggestions = cleanedPredictions;
                    break;
            }
        } catch (error) {
            const responseData = error.response?.data || null;
            Sentry.captureException(error, {
                level: 'error',
                tags: {
                    function: 'getAutocompleteSuggestions',
                    addressType,
                    type
                },
                extra: {
                    input,
                    responseData
                }
            });
        }
    };

    const getPlaceDetails = async (addressType, placeId) => {
        
        Sentry.addBreadcrumb({
            category: 'api',
            message: `Requesting autocompletion details for ${placeId}`,
            level: 'info'
        });

        try {
            const response = await http.get('/api/places/details', { params: { place_id: placeId } });
            const addressComponents = response.data.result.address_components;
            fillAddressFields(addressType, addressComponents);
        } catch (error) {
            const responseData = error.response?.data || null;
            Sentry.captureException(error, {
                level: 'error',
                tags: {
                    function: 'getPlaceDetails',
                    addressType,
                    placeId
                },
                extra: {
                    placeId,
                    responseData
                }
            });
        }
    };

    const selectSuggestion = (addressType, suggestion, type) => {
        getPlaceDetails(addressType, suggestion.place_id);
        resetSuggestions(addressType);
        toggleDropdown(addressType, type, false);
    };

    const resetSuggestions = (addressType) => {
        suggestions.value[addressType].addressCodeSuggestions = [];
        suggestions.value[addressType].citySuggestions = [];
        suggestions.value[addressType].streetNameSuggestions = [];
        suggestions.value[addressType].houseNumberSuggestions = [];
    };

    const fillAddressFields = (addressType, components) => {
        const postalCode = getComponent(components, 'postal_code');
        if (postalCode) {
            tenantStore.setFormValue(addressFieldsMap[addressType].addressCode, postalCode);
            tenantStore.validateField(addressFieldsMap[addressType].addressCode, postalCode);
        }
        const city = getComponent(components, 'locality');
        if (city) {
            tenantStore.setFormValue(addressFieldsMap[addressType].city, city);
            tenantStore.validateField(addressFieldsMap[addressType].city, city);
        }
        const streetName = getComponent(components, 'route');
        if (streetName) {
            tenantStore.setFormValue(addressFieldsMap[addressType].streetName, streetName);
            tenantStore.validateField(addressFieldsMap[addressType].streetName, streetName);
        }
        const streetNumber = getComponent(components, 'street_number');
        if (streetNumber) {
            tenantStore.setFormValue(addressFieldsMap[addressType].houseNumber, streetNumber);
            tenantStore.validateField(addressFieldsMap[addressType].houseNumber, streetNumber);
        }
    };

    const getComponent = (components, type) => {
        const component = components.find(c => c.types.includes(type));
        return component ? component.long_name : '';
    };

    return {
        suggestions,
        isDropdownOpen,
        toggleDropdown,
        getAutocompleteSuggestions,
        selectSuggestion,
        resetSuggestions
    };
});
